import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, Loader2, RotateCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { VersionTimeline } from "@/components/strategy/version-timeline";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { apiService } from "@/lib/api-service";

export const Route = createFileRoute("/_app/_/strategy/versions")({
	component: StrategyVersionsPage,
});

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

function StrategyVersionsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	// Dialog state
	const [revertDialogOpen, setRevertDialogOpen] = useState(false);
	const [selectedVersion, setSelectedVersion] = useState<number | null>(null);

	// -----------------------------------------------------------------------
	// Queries
	// -----------------------------------------------------------------------

	const {
		data: versionsData,
		isLoading,
		isError,
		error,
	} = useQuery({
		queryKey: ["strategy-versions"],
		queryFn: () => apiService.getStrategyVersions(),
	});

	// -----------------------------------------------------------------------
	// Mutations
	// -----------------------------------------------------------------------

	const revertMutation = useMutation({
		mutationFn: (version: number) => apiService.revertStrategyVersion(version),
		onSuccess: (_data, version) => {
			queryClient.invalidateQueries({ queryKey: ["my-strategy"] });
			queryClient.invalidateQueries({ queryKey: ["strategy-status"] });
			queryClient.invalidateQueries({ queryKey: ["strategy-versions"] });
			toast.success(`Reverted to version ${version}`);
			setRevertDialogOpen(false);
			setSelectedVersion(null);
			navigate({ to: "/strategy" });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Failed to revert strategy version.");
		},
	});

	// -----------------------------------------------------------------------
	// Handlers
	// -----------------------------------------------------------------------

	function handleView(_version: number) {
		// Navigate to the reveal page for now — version detail could be a future route
		navigate({ to: "/strategy" });
	}

	function handleRevert(version: number) {
		setSelectedVersion(version);
		setRevertDialogOpen(true);
	}

	function handleRevertConfirm() {
		if (selectedVersion != null) {
			revertMutation.mutate(selectedVersion);
		}
	}

	function handleRevertDialogChange(open: boolean) {
		if (!open) {
			setSelectedVersion(null);
		}
		setRevertDialogOpen(open);
	}

	// -----------------------------------------------------------------------
	// Loading state
	// -----------------------------------------------------------------------

	if (isLoading) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-3xl space-y-6">
					<Skeleton className="h-9 w-48" />
					<Skeleton className="h-5 w-72" />
					<div className="space-y-4">
						{[1, 2, 3].map((n) => (
							<div key={`skel-${n}`} className="flex gap-4">
								<Skeleton className="size-10 rounded-full shrink-0" />
								<Skeleton className="h-32 flex-1" />
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	// -----------------------------------------------------------------------
	// Error state
	// -----------------------------------------------------------------------

	if (isError) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-3xl space-y-6">
					<Button variant="ghost" size="sm" asChild>
						<Link to="/strategy">
							<ChevronLeft
								data-icon="inline-start"
								className="size-4"
							/>
							Back to Strategy
						</Link>
					</Button>
					<div className="text-center py-12 text-muted-foreground">
						<p>Failed to load version history.</p>
						<p className="text-sm mt-1">
							{error?.message || "An unexpected error occurred."}
						</p>
					</div>
				</div>
			</div>
		);
	}

	const versions = versionsData?.versions ?? [];
	const currentVersion = versionsData?.current_version ?? 0;

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-3xl space-y-6">
				{/* Back link */}
				<Button variant="ghost" size="sm" asChild>
					<Link to="/strategy">
						<ChevronLeft
							data-icon="inline-start"
							className="size-4"
						/>
						Back to Strategy
					</Link>
				</Button>

				{/* Header */}
				<div>
					<h1 className="text-2xl font-bold tracking-tight">
						Version History
					</h1>
					<p className="text-sm text-muted-foreground mt-1">
						{versions.length > 0
							? `${versions.length} version${versions.length !== 1 ? "s" : ""} — currently on v${currentVersion}`
							: "No version history available"}
					</p>
				</div>

				{/* Timeline or empty state */}
				{versions.length === 0 ? (
					<div className="text-center py-16 text-muted-foreground">
						<p>No version history available</p>
					</div>
				) : (
					<VersionTimeline
						versions={versions}
						currentVersion={currentVersion}
						onView={handleView}
						onRevert={handleRevert}
					/>
				)}
			</div>

			{/* Revert confirmation dialog */}
			<Dialog open={revertDialogOpen} onOpenChange={handleRevertDialogChange}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<RotateCcw className="size-5 text-primary" />
							Revert to Version {selectedVersion}?
						</DialogTitle>
						<DialogDescription>
							This will create a new version based on version{" "}
							{selectedVersion}. Your current changes won't be lost.
						</DialogDescription>
					</DialogHeader>

					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => handleRevertDialogChange(false)}
							disabled={revertMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							onClick={handleRevertConfirm}
							disabled={revertMutation.isPending}
						>
							{revertMutation.isPending ? (
								<Loader2
									data-icon="inline-start"
									className="size-4 animate-spin"
								/>
							) : (
								<RotateCcw
									data-icon="inline-start"
									className="size-4"
								/>
							)}
							{revertMutation.isPending ? "Reverting..." : "Revert"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
